import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Alert,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TablePagination,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";
import { POPPINS, HEADER_COLOR } from "./adminConstants";

const DRAWER_RPP = 20;

const LoginLogDrawer = ({
  drawerUser,
  drawerRows,
  drawerTotal,
  drawerPage,
  drawerLoading,
  drawerError,
  onClose,
  onPageChange,
}) => (
  <Drawer
    anchor="right"
    open={!!drawerUser}
    onClose={onClose}
    PaperProps={{ sx: { width: { xs: "100vw", sm: 520 }, display: "flex", flexDirection: "column" } }}
  >
    {/* Header */}
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1,
      px: 2.5, py: 2, borderBottom: "1px solid #e0e0e0",
      backgroundColor: "#f5f7fb",
    }}>
      <LoginIcon sx={{ color: "#1976d2", fontSize: 22 }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" sx={{ ...POPPINS, fontWeight: 700, color: HEADER_COLOR, lineHeight: 1.2 }}>
          Login History
        </Typography>
        <Typography variant="caption" sx={{ ...POPPINS, color: "#777" }}>
          {drawerUser?.name} &bull; {drawerUser?.email_id}
        </Typography>
      </Box>
      <IconButton size="small" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>

    {/* Body */}
    <Box sx={{ flex: 1, overflow: "auto" }}>
      {drawerLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {drawerError && (
        <Alert severity="error" sx={{ m: 2, ...POPPINS }}>{drawerError}</Alert>
      )}

      {!drawerLoading && !drawerError && drawerRows.length === 0 && (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={POPPINS}>
            No login records found.
          </Typography>
        </Box>
      )}

      {!drawerLoading && drawerRows.length > 0 && (
        <List disablePadding>
          {drawerRows.map((log, i) => {
            const success = (log.login_status || "").toLowerCase() === "success";
            return (
              <ListItem
                key={log.login_id || i}
                divider
                sx={{ px: 2.5, py: 1.25, alignItems: "flex-start", backgroundColor: success ? "transparent" : "#fff8f8" }}
              >
                <ListItemText
                  disableTypography
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                      <Chip
                        label={success ? "Success" : (log.login_status || "Failed")}
                        size="small"
                        color={success ? "success" : "error"}
                        variant={success ? "filled" : "outlined"}
                        sx={{ ...POPPINS, fontSize: "0.65rem", height: 20 }}
                      />
                      <Typography variant="body2" sx={{ ...POPPINS, fontSize: "0.78rem", color: "#444" }}>
                        {log.login_timestamp ? new Date(log.login_timestamp).toLocaleString() : "—"}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 0.25 }}>
                      {log.ip_address && (
                        <Typography variant="caption" sx={{ ...POPPINS, color: "#888" }}>
                          IP: {log.ip_address}
                        </Typography>
                      )}
                      {!success && log.failure_reason && (
                        <Typography variant="caption" sx={{ ...POPPINS, color: "#c62828" }}>
                          Reason: {log.failure_reason}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>

    {/* Pagination */}
    {drawerTotal > DRAWER_RPP && (
      <Box sx={{ borderTop: "1px solid #e0e0e0" }}>
        <TablePagination
          component="div"
          count={drawerTotal}
          page={drawerPage}
          onPageChange={onPageChange}
          rowsPerPage={DRAWER_RPP}
          rowsPerPageOptions={[DRAWER_RPP]}
          sx={{ ...POPPINS, fontSize: "0.75rem" }}
        />
      </Box>
    )}
  </Drawer>
);

export default LoginLogDrawer;
